"use client";

/**
 * DayBeatBlock — one horizontal "day in the life" beat on Scene 4 Your Path.
 *
 * Composition per concept-v2 §4.5:
 *   - amber day-chip (top, font-mono uppercase tracked, e.g. "DAY 0 + 20 MIN")
 *   - body copy (Inter 18-20px OR italic serif for voice beats)
 *   - preview image (real artifact screenshot, tinted amber shadow, subtle rotate)
 *
 * Layout alternates left/right on desktop — controlled via `reverse` prop.
 * On mobile (<md) everything stacks vertically with image ABOVE text per spec.
 *
 * Motion:
 *   - whileInView fade-up with margin -80px and 150ms stagger between siblings
 *     (parent controls the per-block delay)
 *   - hover: preview rotates slightly + scales 1.02 + amber glow — Framer
 *     Motion whileHover
 *   - reduced-motion: all of the above collapse to instant opacity swap
 *
 * a11y:
 *   - heading level configurable via `headingId` (each block links to
 *     <SceneContainer> via aria-labelledby chain)
 *   - image alt text required (from PREVIEW_IMAGES registry)
 *   - focusable preview (tabIndex=0 on wrapper) — amber ring on focus-visible
 */

import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { PreviewImage, PreviewSequence } from "../data/previewImages";
import { MiniFoilRow } from "./MiniFoilRow";
import { PreviewCarousel } from "./PreviewCarousel";

const APPLE_EASE_OUT = [0.16, 1, 0.3, 1] as const;

interface DayBeatBlockProps {
  /** Chip text — e.g. "DAY 0", "DAY 0 + 20 MIN", "DAY 9", "DAY 365". */
  dayLabel: string;
  /** Body copy — string paragraphs or composed nodes. */
  children: ReactNode;
  /**
   * Preview artifact(s) shown opposite the text. A single `PreviewImage` renders
   * the legacy still frame; a `PreviewSequence` (≥2 frames) flips into the
   * auto-advancing PreviewCarousel for a story-arc tour. MUTUALLY EXCLUSIVE
   * with `badges`. Omit both for text-only blocks (Day 9 + Day 365 per
   * wireframe §6.2 block-4 / block-5 variants — "visual silence = honesty",
   * text carries the weight).
   */
  preview?: PreviewImage | PreviewSequence;
  /** Optional a11y label for carousel-mode preview region. */
  previewAriaLabel?: string;
  /**
   * Mini foil grade badges rendered in place of the preview (Day 0 variant).
   * Spec §6.2 block-1: `['A+', 'B+', 'A-']` — 3 grades, first is top pick.
   * MUTUALLY EXCLUSIVE with `preview`.
   */
  badges?: readonly string[];
  /**
   * Flip layout: preview on LEFT (desktop). Default false → preview on RIGHT.
   * On mobile the layout always stacks image above text regardless.
   */
  reverse?: boolean;
  /**
   * Rotation direction for subtle editorial tilt (degrees).
   * Default: -2deg on even-indexed blocks, -1deg on odd — caller decides.
   */
  rotateDeg?: number;
  /** Animation entrance delay in seconds — parent-controlled stagger. */
  delay?: number;
  /** Extra classes on outer wrapper. */
  className?: string;
}

export function DayBeatBlock({
  dayLabel,
  children,
  preview,
  previewAriaLabel,
  badges,
  reverse = false,
  rotateDeg = -2,
  delay = 0,
  className,
}: DayBeatBlockProps) {
  const reduced = useReducedMotion();

  const entrance = reduced
    ? { initial: false }
    : {
        initial: { opacity: 0, y: 32 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-80px" },
        transition: { duration: 0.6, ease: APPLE_EASE_OUT, delay },
      };

  // Text-only variant (Day 9 + Day 365) — spec §6.2 forbids preview here.
  // Visual silence = honesty. Text takes full width, centered max-w-2xl.
  const isTextOnly = !preview && !badges;

  if (isTextOnly) {
    return (
      <motion.article
        {...entrance}
        className={cn(
          "mx-auto flex w-full max-w-2xl flex-col items-center gap-4",
          "text-center",
          "max-md:items-start max-md:text-left",
          className,
        )}
      >
        <span
          aria-hidden
          className="font-mono text-[12px] font-medium uppercase tracking-[0.2em] text-amber-400/80"
        >
          {dayLabel}
        </span>
        <div className="text-[16px] leading-[1.55] text-white/80 md:text-[19px] md:leading-[1.5]">
          {children}
        </div>
      </motion.article>
    );
  }

  // Desktop layout direction (flex-row vs flex-row-reverse).
  // Mobile ALWAYS stacks image/badges above text (flex-col) per spec.
  const desktopDir = reverse ? "md:flex-row-reverse" : "md:flex-row";

  return (
    <motion.article
      {...entrance}
      className={cn(
        "flex flex-col items-stretch gap-8 md:gap-12",
        desktopDir,
        className,
      )}
    >
      {/* Visual column — 60% desktop, full mobile. Rendered first so mobile stacks on top. */}
      <div
        className={cn(
          "order-1 w-full md:w-3/5",
          badges && "flex items-center justify-center md:justify-start",
        )}
      >
        {preview ? (
          <PreviewCarousel
            frames={
              Array.isArray(preview)
                ? (preview as PreviewSequence)
                : [preview as PreviewImage]
            }
            rotateDeg={rotateDeg}
            ariaLabel={previewAriaLabel}
          />
        ) : badges ? (
          <MiniFoilRow grades={badges} />
        ) : (
          <PreviewPending />
        )}
      </div>

      {/* Text column — 40% desktop, full mobile (centered). */}
      <div
        className={cn(
          "order-2 flex w-full flex-col justify-center gap-4 md:w-2/5",
          "text-left",
        )}
      >
        <span
          aria-hidden
          className="font-mono text-[12px] font-medium uppercase tracking-[0.2em] text-amber-400/80"
        >
          {dayLabel}
        </span>
        <div className="text-[16px] leading-[1.55] text-white/80 md:text-[19px] md:leading-[1.5]">
          {children}
        </div>
      </div>
    </motion.article>
  );
}

/* ──────────────────────────── Preview pending ──────────────────────────── */

function PreviewPending() {
  return (
    <div
      aria-hidden
      className={cn(
        "flex aspect-[16/10] w-full items-center justify-center",
        "rounded-2xl border border-white/10 bg-amber-400/10",
        "ring-1 ring-inset ring-white/5",
      )}
    >
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/30">
        preview pending
      </span>
    </div>
  );
}
